import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tabs,
  TabsProps,
  Tag,
} from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TimelineContainer from "./Components/Timeline";
import "./PatientProfile.css";
import AddPatientCostsModal from "../../Components/Modals/AddPatientCosts/AddPatientCosts";
import PatientStatistic from "../../Components/PatientStatistic/PatientStatistic";
import type { ColumnsType } from "antd/es/table";
import { BASE_API_URL, constants } from "../../utils/consts";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import UploadPatientPhotos from "./UploadPhotos/UploadPhotos";
import { setLoader } from "../../utils/redux/slices/loaderSlice";
import { Select } from "antd";
import mAvatar from "../../assets/m-avatar.png";
import wAvatar from "../../assets/w-avatar.png";
import ToothSelector from "../../Components/ToothSelector/ToothSelector";
import { ToothData } from "../../Components/Tooth/ToothBox";
import ToothChart from "../../Components/Tooth/ToothChart";
import NotesExportPrint from "./Components/ExportPrint/ExportPrint";
import { usePermissions } from "../../utils/usePermissions";
import { message } from "antd";
type Props = {};
const { Option } = Select;

interface FieldData {
  name: string | number | (string | number)[];
  value?: any;
  touched?: boolean;
  validating?: boolean;
  errors?: string[];
}
interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const createInitialTeeth = (): ToothData[] =>
  Array.from({ length: 32 }, (_, i) => ({
    id: i + 1,
    missing: false,
    parts: {
      center: null,
      top: null,
      right: null,
      bottom: null,
      left: null,
    },
  }));

const PatiendProfile: React.FC<Props> = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const [patientCostsModal, setPatientCostsModal] = useState<boolean>(false);
  const [totalPaid, setTotalPaid] = useState<any>(0);
  const [needToPaid, setNeedToPaid] = useState<any>(0);
  const [totalSum, setTotalSum] = useState<any>(0);
  const [staffMembers, setStaffMembers] = useState<any>(undefined);
  const [patient, setPatient] = useState<any>(undefined);
  const [billing, setBilling] = useState<any>(undefined);
  const userData = useSelector((state: any) => state.userData.user);
  const [form] = Form.useForm(); // Create a form instance using Form.useForm()
  const [teeth, setTeeth] = useState<ToothData[]>(createInitialTeeth());
  const { hasPermission } = usePermissions();
  const canView = hasPermission("patient-profile:view");
  const canUpdateInfo = hasPermission("patient-profile:update-info");
  const canManageCosts = hasPermission("patient-profile:manage-costs");
  const canUploadPhotos = hasPermission("patient-profile:upload-photos");
  const canEditTeeth = hasPermission("patient-profile:edit-tooth-chart");
  const canTimelineCreate = hasPermission(
    "patient-profile:timeline:create-note"
  );
  const canTimelineUpdate = hasPermission(
    "patient-profile:timeline:update-note"
  );
  const canTimelineDelete = hasPermission(
    "patient-profile:timeline:delete-note"
  );
  const canTimelineExport = hasPermission(
    "patient-profile:timeline:export"
  );

  const [color, setColor] = useState<"felling" | "caries">("caries");
  const calculateCurrency = () => {
    if (userData.client.currency === "Dinar") {
      return "RSD";
    } else if (userData.client.currency === "Euro") {
      return "EUR";
    } else {
      return "$";
    }
  };
  const columns: ColumnsType<DataType> = [
    {
      title: t("Costs"),
      dataIndex: "charges",
      key: "charges",
      render: (text) => (
        <>
          {text} {calculateCurrency()}
        </>
      ),
    },
    {
      title: t("Date"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value, row) => {
        return format(new Date(value), "d-MMM-yyyy");
      },
    },
    {
      title: t("Action"),
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {/* <a>Invite {record.name}</a> */}
          {/* <a>Delete</a> */}
        </Space>
      ),
    },
  ];
  useEffect(() => {
    if (userData.clientId) {
      dispatch(setLoader(true));
      axios
        .get(`${BASE_API_URL}/${userData.clientId}/users`)
        .then((res) => {
          setStaffMembers(res.data);
          setTeeth(JSON.parse(res.data?.teeth));
        })
        .catch((err) => console.error(err))
        .finally(() => dispatch(setLoader(false)));
    }
  }, [userData]);
  useEffect(() => {
    axios
      .get(`${BASE_API_URL}/${params.id}/billings`)
      .then((res) => {
        setBilling(res.data);
        const totalCharges = res.data.reduce(
          (total: any, billing: any) => total + billing.charges,
          0
        );
        setTotalSum(totalCharges);
        setNeedToPaid(totalCharges - totalPaid);
      })
      .catch((err) => console.error(err));
  }, [totalPaid, params.id]);
  useEffect(() => {
    axios
      .get(`${BASE_API_URL}/patient/${params.id}`)
      .then((res) => {
        setPatient(res.data);
        setTeeth(JSON.parse(res.data?.teeth));
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    // Set form initial values when activeNotes changes
    if (patient && totalSum) {
      form.setFieldsValue({
        costs: totalSum,
      });
    } else {
      // Reset form fields if activeNotes is null or undefined
      form.resetFields();
    }
  }, [patient, form, totalSum]);

  const onFinish = async (values: any) => {
    if (!canUpdateInfo) {
      message.warning(t("No permission to update patient"));
      return;
    }
    axios
      .put(`${BASE_API_URL}/patient/${params.id}`, values)
      .then((res) => {
        axios
          .get(`${BASE_API_URL}/patient/${params.id}`)
          .then((res) => {
            setPatient(res.data);
          })
          .catch((err) => console.error(err));
      })
      .catch((err) => console.error(err));
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error("Failed:", errorInfo);
  };

  const hidePatientCostModal = () => {
    if (!canManageCosts) {
      message.warning(t("No permission to manage costs"));
      return;
    }
    setPatientCostsModal(!patientCostsModal);
  };

  const onUpdateTimelineCosts = (costs: any) => {
    setTotalPaid(costs);
    setNeedToPaid(totalSum - costs);
  };

  const onAddPationCosts = (costs: any) => {
    if (!canManageCosts) {
      message.warning(t("No permission to manage costs"));
      return;
    }
    if (userData) {
      axios
        .post(`${BASE_API_URL}/billing/create`, {
          userId: userData.id,
          patientId: params.id,
          name: patient?.name ?? "",
          doctorName: userData.username,
          charges: costs,
        })
        .then((res) => {
          axios
            .get(`${BASE_API_URL}/${params.id}/billings`)
            .then((res) => {
              // setPatient(res.data)
              setBilling(res.data);
              setPatientCostsModal(!patientCostsModal);
              const totalCharges = res.data.reduce(
                (total: any, billing: any) => total + billing.charges,
                0
              );
              setTotalSum(totalCharges);
              setNeedToPaid(totalCharges - totalPaid);
              form.resetFields();
            })
            .catch((err) => console.error(err));
        })
        .catch((err) => console.error(err));
    }
  };

  const onUploadPhotos = (values: any) => {
    if (!canUploadPhotos) {
      message.warning(t("No permission to upload photos"));
      return;
    }
    const formData = new FormData();
    formData.append("file", values.file[0].originFileObj);
    axios
      .put(`${BASE_API_URL}/patient/${params.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        setPatient(res.data.patient);
      })
      .catch((error) => {
        console.error("Error during upload:", error);
      });
  };

  const onChange = (key: string) => {
    console.log(key);
  };

  const handleTeethChange = (updated: ToothData[]) => {
    setTeeth(updated);
    const stringO = JSON.stringify(updated);
    localStorage.setItem("dddddd", stringO);
    let arr: any = localStorage.getItem("dddddd");
  };

  const items: TabsProps["items"] = [
    {
      key: t("Total Costs"),
      label: t("Total Costs"),
      children: (
        <div className="billingTableWrap">
          <Table
            columns={columns}
            dataSource={billing}
            className="airbnb-table"
            size="small"
            pagination={false}
            scroll={{ y: 260 }}
          />
          <Button onClick={hidePatientCostModal} disabled={!canManageCosts}>
            + {t("Add Costs")}
          </Button>
        </div>
      ),
    },
    {
      key: t("Photo"),
      label: t("Photo"),
      children: (
        <Row>
          <Col xs={24}>
            {canUploadPhotos ? (
              <>
                {t("Uploaded photos")}
                <UploadPatientPhotos
                  onUploadPhotos={onUploadPhotos}
                  patient={patient}
                />
              </>
            ) : (
              <div>{t("No permission to upload photos")}</div>
            )}
          </Col>
        </Row>
      ),
    },
  ];

  const handleSelection = (selected: any) => {
    console.log("Selektovane površine:", selected);
  };

  const saveTeeth = () => {
    if (!canEditTeeth) {
      message.warning(t("No permission to edit tooth chart"));
      return;
    }
    dispatch(setLoader(true));
    axios
      .put(`${BASE_API_URL}/patient/${params.id}`, {
        teeth: JSON.stringify(teeth),
      })
      .then((res) => {
        axios
          .get(`${BASE_API_URL}/patient/${params.id}`)
          .then((res) => {
            setPatient(res.data);
            setTeeth(JSON.parse(res.data?.teeth));
          })
          .catch((err) => console.error(err));
      })
      .catch((err) => console.error(err))
      .finally(() => dispatch(setLoader(false)));
  };

  if (!canView) {
    return <div className="mainContainer">{t("No permission to view")}</div>;
  }
  if (!patient) {
    return <></>;
  }
  return (
    <div className="patientProfilePage">
      <div className="profileHeader airbnb-card">
        <div className="profileHeader__main">
          <div className="userPhoto">
            {patient?.user?.avatar?.url ? (
              <img src={patient?.user?.avatar?.url} alt="Profile" />
            ) : patient?.gender === "male" ? (
              <img src={mAvatar} alt="Profile" />
            ) : patient?.gender === "female" ? (
              <img src={wAvatar} alt="Profile" />
            ) : (
              <div className="photoPlaceholder" />
            )}
          </div>
          <div className="userInfo">
            <div className="pill">{t("Patient Profile")}</div>
            <div className="name">{patient?.name}</div>
            <div className="meta">
              {patient?.email && <span>{patient.email}</span>}
              {patient?.phoneNo && <span>{patient.phoneNo}</span>}
              {patient?.address && <span>{patient.address}</span>}
            </div>
            <Space size={[8, 8]} wrap className="chips">
              {patient?.gender && (
                <Tag color="magenta">{t(patient.gender)}</Tag>
              )}
              {patient?.jmbg && <Tag color="blue">{patient.jmbg}</Tag>}
            </Space>
          </div>
        </div>
        <div className="profileHeader__aside">
          <div className="statBox">
            <span className="eyebrow">{t("Total Costs")}</span>
            <strong>{totalSum || 0} {calculateCurrency()}</strong>
          </div>
          <div className="statBox">
            <span className="eyebrow">{t("Need to pay")}</span>
            <strong>{needToPaid || 0} {calculateCurrency()}</strong>
          </div>
        </div>
      </div>
      <Row gutter={24} className="profileContent">
        <Col xs={24} lg={13}>
          <div className="childBodyContainer patientProfileBody airbnb-card">
            <div className="sectionHeader">
              <div>
                <span className="eyebrow">{t("Basics")}</span>
                <h3>{t("User Info")}</h3>
                <p className="sectionSub">
                  {t("Keep patient contact and personal details in sync.")}
                </p>
              </div>
              <Button
                type="default"
                className="scrollToToothBtn"
                onClick={() =>
                  document
                    .getElementById("tooth-section-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                {t("Jump to teeth")}
              </Button>
            </div>
            <Form
              disabled={!canUpdateInfo}
              name="global_state"
              className="patientForm"
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    initialValue={patient?.name ?? ""}
                    label={t("Name")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    initialValue={patient?.email ?? ""}
                    label="Email"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="gender"
                    initialValue={patient?.gender ?? ""}
                    label={t("Gender")}
                  >
                    <Select placeholder={t("Select Gender")} allowClear>
                      <Option value="male">{t("Male")}</Option>
                      <Option value="female">{t("Female")}</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="jmbg"
                    initialValue={patient?.jmbg ?? ""}
                    label={t("Social Number")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="address"
                    initialValue={patient?.address ?? ""}
                    label={t("Address")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phoneNo"
                    initialValue={patient?.phoneNo ?? ""}
                    label={t("Phone Number")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="costs" label={t("Total Costs")}>
                <Input disabled={true} />
              </Form.Item>
              <div className="formActions">
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!canUpdateInfo}
                >
                  {t("Save")}
                </Button>
              </div>
            </Form>
          </div>

          <div className="childBodyContainer airbnb-card">
            <div className="sectionHeader">
              <div>
                <span className="eyebrow">{t("Records")}</span>
                <h3>{t("Billing & Media")}</h3>
                <p className="sectionSub">
                  {t("Track payments and manage shared patient photos.")}
                </p>
              </div>
              <div className="statRow">
                <div className="pill pill-soft">
                  {t("Total Costs")}: {totalSum} {calculateCurrency()}
                </div>
                <div className="pill pill-soft">
                  {t("Need to pay")}: {needToPaid} {calculateCurrency()}
                </div>
              </div>
            </div>
            <Tabs
              defaultActiveKey="1"
              items={items}
              onChange={onChange}
              className="cozyTabs compactTabs"
            />
          </div>
          <AddPatientCostsModal
            isOpen={patientCostsModal}
            onCloseModal={hidePatientCostModal}
            onUpdateCosts={onAddPationCosts}
          />
        </Col>
        <Col xs={24} lg={11} className="profileTimeline">
          <PatientStatistic totalPaid={totalPaid} needToPaid={needToPaid} />
          <div className="childBodyContainer airbnb-card timelineCard">
            <div className="sectionHeader">
              <div>
                <span className="eyebrow">{t("Timeline")}</span>
                <h3>{t("Notes & Activity")}</h3>
              </div>
            </div>
            <div className="timelineScroll">
              <TimelineContainer
                staffMembers={staffMembers}
                updatedCosts={onUpdateTimelineCosts}
                patient={patient}
                userData={userData}
              />
            </div>
          </div>
        </Col>
      </Row>

      <Row gutter={24} className="profileContent fullWidthBlock" id="tooth-section-anchor">
        <Col span={24}>
          <div className="toothSelector childBodyContainer airbnb-card">
            <Row gutter={16}>
              <div>
                <div className="sectionHeader">
                  <div>
                    <span className="eyebrow">{t("Dentistry")}</span>
                    <h3>{t("Tooth Chart")}</h3>
                    <p className="sectionSub">
                      {t("Visualize work with a clean, interactive chart.")}
                    </p>
                  </div>
                  <div className="pill pill-soft">{t("Interactive")}</div>
                </div>
                <div className="pickerRow">
                  <label>{t("Color")}</label>
                  <Select
                    placeholder={t("Please select type")}
                    onChange={(e) => setColor(e as any)}
                    value={color}
                  >
                    <Option value={"felling"} key={"felling"}>
                      {t("Felling")}
                    </Option>
                    <Option value={"caries"} key={"caries"}>
                      {t("Caries")}
                    </Option>
                  </Select>
                </div>

                <ToothChart
                  teeth={teeth}
                  currentColor={color}
                  onChange={(updated) => {
                    if (!canEditTeeth) return;
                    handleTeethChange(updated);
                  }}
                />
                <Button
                  type="primary"
                  className="saveTeethBtn"
                  onClick={() => saveTeeth()}
                  disabled={!canEditTeeth}
                >
                  {t("Save")}
                </Button>
              </div>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};
export default PatiendProfile;
